import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useScreenInit } from '../useScreenInit';
const servicesList = [{
  id: 'signature-drinks',
  title: 'Signature Drinks',
  subtitle: 'Cocktails, Mocktails & Smoothies',
  description: "Expertly crafted cocktails, sophisticated mocktails, and refreshing smoothies tailored to your event's unique vibe. From bold spirits to vibrant alcohol-free blends, we deliver excellence in every glass, ensuring a one-of-a-kind experience for every guest.",
  image: 'https://cdn.magicpatterns.com/uploads/kHZKGpuftEu32wyoQC1Vk4/image.png',
  features: ['Signature Cocktails', 'Artisanal Mocktails', 'Fresh Smoothies', 'Premium Ingredients']
}, {
  id: 'desserts',
  title: 'Gourmet Desserts',
  subtitle: 'The perfect sweet finish',
  description: 'Indulge in a luxurious selection of shooters, artisanal cake pops, and fresh fruit cups designed for the perfect sweet finish. Each dessert is a masterpiece of flavor and presentation, providing a touch of luxury to your celebration.',
  image: 'https://cdn.magicpatterns.com/uploads/haGyB1F9QNH8WgNaXRiVbc/ChatGPT_Image_Jun_17,_2026,_06_02_59_PM.png',
  features: ['Shooter Desserts', 'Artisanal Cake Pops', 'Fresh Fruit Cups', 'Elegant Presentation']
}, {
  id: 'bites',
  title: 'Savory Bites',
  subtitle: 'Gourmet Bites & More',
  description: 'Savory delights featuring our signature shawarma and gourmet bites, prepared with premium ingredients and impeccable flair. Our bites are designed to complement your drink selection and elevate the overall hospitality of your event.',
  image: 'https://cdn.magicpatterns.com/uploads/6nzZm75QAGWKmcR2E8PxBe/image.png',
  features: ['Signature Shawarma', 'Gourmet Bites', 'Premium Ingredients', 'Impeccable Flair']
}];
export function Services() {
  useScreenInit();
  const {
    hash
  } = useLocation();
  const {
    scrollYProgress
  } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="min-h-screen bg-black selection:bg-[#992f74] selection:text-white" data-id="element-453">
      <Header data-id="element-454" />

      <main className="bg-black" data-id="element-455">
        {/* Cinematic Hero */}
        <section className="relative w-full h-[80vh] flex items-center justify-center overflow-hidden" data-id="element-456">
          <motion.div style={{
          y
        }} className="absolute inset-0 z-0" data-id="element-457">
            <img src="https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&q=80&w=2070" alt="Services Hero" className="w-full h-full object-cover opacity-40" data-id="element-458" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black" data-id="element-459"></div>
          </motion.div>

          <div className="relative z-10 text-center px-6 mt-20" data-id="element-460">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} data-id="element-461">
              <h1 className="text-5xl md:text-8xl font-montserrat font-black italic text-white mb-6 drop-shadow-2xl uppercase tracking-tighter" data-id="element-462">
                Our <span className="text-[#992f74]" data-id="element-463">Offerings</span>
              </h1>
              <p className="text-sm md:text-base text-white/60 font-light tracking-[0.5em] uppercase" data-id="element-464">
                Luxury in Every Sip & Bite
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Showcase */}
        <section className="py-16 px-6 relative z-20 -mt-24 bg-black" data-id="element-465">
          <div className="max-w-7xl mx-auto space-y-32" data-id="element-466">
            {servicesList.map((service, index) => <div key={service.id} id={service.id} className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-24 items-center scroll-mt-32`} data-id="element-467">
                {/* Image Side */}
                <motion.div initial={{
              opacity: 0,
              x: index % 2 === 1 ? 20 : -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 1.2,
              ease: 'easeOut'
            }} className="w-full lg:w-1/2 relative" data-id="element-468">
                  <div className="aspect-[4/5] w-full max-w-[340px] mx-auto relative rounded-t-full rounded-b-2xl overflow-hidden border border-white/5 shadow-[0_0_50px_rgba(153,47,116,0.05)] group" data-id="element-469">
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-700 z-10" data-id="element-470"></div>
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000 ease-out" data-id="element-471" />
                  </div>
                  {/* Decorative Accent */}
                  <div className={`absolute top-1/2 -translate-y-1/2 ${index % 2 === 1 ? '-left-12' : '-right-12'} w-24 h-24 border border-[#992f74]/20 rounded-full hidden lg:block animate-pulse`} data-id="element-472"></div>
                </motion.div>

                {/* Text Side */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 1.2,
              ease: 'easeOut',
              delay: 0.3
            }} className="w-full lg:w-1/2 space-y-8 text-center lg:text-left" data-id="element-473">
                  <div data-id="element-474">
                    <h3 className="text-[#992f74] text-[10px] font-bold tracking-[0.6em] uppercase mb-4 font-montserrat" data-id="element-475">
                      {service.subtitle}
                    </h3>
                    <h2 className="text-4xl md:text-5xl font-montserrat font-black italic text-white uppercase tracking-tighter" data-id="element-476">
                      {service.title}
                    </h2>
                  </div>

                  <p className="text-white/60 text-base md:text-lg font-light leading-relaxed max-w-xl mx-auto lg:mx-0" data-id="element-477">
                    {service.description}
                  </p>

                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 pt-8 border-t border-white/10" data-id="element-478">
                    {service.features.map((feature, i) => <li key={i} className="flex items-center justify-center lg:justify-start gap-3 text-white/70" data-id="element-479">
                        <span className="text-[#992f74] text-xs" data-id="element-480">✦</span>
                        <span className="uppercase tracking-[0.2em] text-[10px] font-bold" data-id="element-481">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <div className="pt-8" data-id="element-482">
                    <a href="/book" className="inline-flex items-center gap-4 text-[#992f74] text-[10px] font-bold tracking-[0.3em] uppercase hover:text-white transition-colors group" data-id="element-483">
                      Inquire Now
                      <span className="w-8 h-[1px] bg-[#992f74] group-hover:bg-white group-hover:w-12 transition-all duration-300" data-id="element-484"></span>
                    </a>
                  </div>
                </motion.div>
              </div>)}
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-32 px-6 bg-black text-center border-t border-white/5" data-id="element-485">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="max-w-3xl mx-auto space-y-8" data-id="element-486">
            <h2 className="text-3xl md:text-4xl font-montserrat font-black italic text-white tracking-wide uppercase" data-id="element-487">
              Ready to elevate your celebration?
            </h2>
            <p className="text-white/50 text-base md:text-lg font-light" data-id="element-488">
              Let us craft a bespoke menu that your guests will talk about long
              after the last toast.
            </p>
            <a href="/book" className="inline-block bg-[#992f74] text-white px-10 py-4 text-[10px] font-bold tracking-[0.4em] uppercase hover:bg-white hover:text-[#992f74] transition-colors duration-300 shadow-2xl mt-8" data-id="element-489">
              Book Your Experience
            </a>
          </motion.div>
        </section>
      </main>
      <Footer data-id="element-490" />
    </motion.div>;
}